import Papa from 'papaparse'

/**
 * Trend Data Utilities
 * Functions for loading and processing trend data for narratives
 */

// Trend data file paths
const TREND_FILES = {
  secondaryEnrollment: '/data/trends/kayunga_learners_enrolment_secondary.csv',
  primaryEnrollment: '/data/trends/masindi_learners_enrolment_primary.csv',
  institutionCount: '/data/trends/number_of_institutions.csv',
  plePerformance: '/data/trends/ple_analysis.csv',
}

/**
 * Load trend data from CSV file
 * @param {string} trendType - Type of trend data to load
 * @param {string} locationCode - Location code to filter by (e.g., 'D01', 'D01S01')
 * @returns {Promise<Array>} Parsed and filtered trend data
 */
export async function loadTrendData(trendType, locationCode) {
  const filePath = TREND_FILES[trendType]
  if (!filePath) {
    console.error(`Unknown trend type: ${trendType}`)
    return []
  }

  try {
    const response = await fetch(filePath)
    if (!response.ok) {
      throw new Error(`Failed to fetch ${filePath}: ${response.statusText}`)
    }

    const csvText = await response.text()

    return new Promise((resolve, reject) => {
      Papa.parse(csvText, {
        header: true,
        dynamicTyping: false,
        skipEmptyLines: true,
        complete: results => {
          if (results.errors.length > 0) {
            console.warn(`Parsing warnings for ${filePath}:`, results.errors)
          }

          // Filter by location code if provided
          let filteredData = results.data
          if (locationCode) {
            filteredData = filteredData.filter(
              row =>
                row.location_code && row.location_code.startsWith(locationCode)
            )
          }

          resolve(filteredData)
        },
        error: error => {
          console.error(`Error parsing ${filePath}:`, error)
          reject(error)
        },
      })
    })
  } catch (error) {
    console.error(`Error loading trend data (${trendType}):`, error)
    return []
  }
}

/**
 * Load all available trends for a location
 * @param {string} locationCode - Location code (e.g., 'D01')
 * @param {string} district - District name for district-specific trend files
 * @returns {Promise<Object>} Object with all available trend data
 */
export async function loadAllTrendsForLocation(locationCode, district) {
  const trends = {}

  try {
    // Load institution count (available for all districts)
    trends.institutionCount = await loadTrendData(
      'institutionCount',
      locationCode
    )

    // Load district-specific enrollment data
    if (district === 'Kayunga') {
      trends.secondaryEnrollment = await loadTrendData(
        'secondaryEnrollment',
        locationCode
      )
    } else if (district === 'Masindi') {
      trends.primaryEnrollment = await loadTrendData(
        'primaryEnrollment',
        locationCode
      )
    }

    // Load PLE data (available for both districts)
    trends.plePerformance = await loadTrendData('plePerformance', locationCode)

    return trends
  } catch (error) {
    console.error('Error loading trends for location:', error)
    return {}
  }
}

/**
 * Aggregate trend data to location level
 * @param {Array} rawData - Raw trend data array
 * @param {string} locationType - Type of location (district, subcounty, parish)
 * @returns {Array} Aggregated trend data
 */
// eslint-disable-next-line no-unused-vars
export function aggregateTrendData(rawData, locationType = 'district') {
  if (!rawData || rawData.length === 0) return []

  // For enrollment trends, aggregate by year and gender
  const aggregated = rawData.reduce((acc, record) => {
    const key = `${record.year}_${record.gender || 'total'}`
    if (!acc[key]) {
      acc[key] = {
        year: record.year,
        gender: record.gender,
        total: 0,
      }
    }
    acc[key].total += parseFloat(record.total) || 0
    return acc
  }, {})

  return Object.values(aggregated).sort((a, b) => a.year - b.year)
}

/**
 * Calculate growth metrics from trend data
 * @param {Array} trendData - Time-series data with year and value
 * @returns {Object} Growth metrics (CAGR, total growth, peak year, etc.)
 */
export function calculateGrowthMetrics(trendData) {
  if (!trendData || trendData.length < 2) {
    return {
      cagr: 0,
      totalGrowth: 0,
      peakYear: null,
      peakValue: 0,
      averageValue: 0,
    }
  }

  // Sort by year
  const sorted = [...trendData].sort((a, b) => a.year - b.year)

  // Calculate total values per year
  const yearlyTotals = sorted.reduce((acc, record) => {
    const year = record.year
    const value = parseFloat(record.total || record.value || 0)
    acc[year] = (acc[year] || 0) + value
    return acc
  }, {})

  const years = Object.keys(yearlyTotals).sort()
  const values = years.map(year => yearlyTotals[year])

  const firstValue = values[0]
  const lastValue = values[values.length - 1]
  const numYears = years.length - 1

  // Calculate CAGR (Compound Annual Growth Rate)
  const cagr =
    firstValue > 0 && lastValue > 0
      ? (Math.pow(lastValue / firstValue, 1 / numYears) - 1) * 100
      : 0

  // Calculate total growth percentage
  const totalGrowth =
    firstValue > 0 ? ((lastValue - firstValue) / firstValue) * 100 : 0

  // Find peak year and value
  let peakYear = years[0]
  let peakValue = values[0]
  values.forEach((value, index) => {
    if (value > peakValue) {
      peakValue = value
      peakYear = years[index]
    }
  })

  // Calculate average
  const averageValue = values.reduce((sum, val) => sum + val, 0) / values.length

  return {
    cagr: cagr.toFixed(2),
    totalGrowth: totalGrowth.toFixed(2),
    peakYear,
    peakValue: Math.round(peakValue),
    averageValue: Math.round(averageValue),
    startYear: years[0],
    endYear: years[years.length - 1],
    startValue: Math.round(firstValue),
    endValue: Math.round(lastValue),
  }
}

/**
 * Filter trend data by year range
 * @param {Array} trendData - Trend data array
 * @param {number|string} startYear - Start year (inclusive)
 * @param {number|string} endYear - End year (inclusive)
 * @returns {Array} Filtered trend data
 */
export function filterTrendsByYears(trendData, startYear, endYear) {
  if (!trendData || trendData.length === 0) return []

  const start = parseInt(startYear)
  const end = parseInt(endYear)

  return trendData.filter(record => {
    const year = parseInt(record.year)
    return year >= start && year <= end
  })
}

/**
 * Get trend summary statistics
 * @param {Array} trendData - Trend data array
 * @returns {Object} Summary statistics
 */
export function getTrendSummary(trendData) {
  if (!trendData || trendData.length === 0) {
    return {
      years: [],
      totalRecords: 0,
      yearRange: '',
      hasData: false,
    }
  }

  const years = [...new Set(trendData.map(r => r.year))].sort()

  return {
    years,
    totalRecords: trendData.length,
    yearRange: `${years[0]}-${years[years.length - 1]}`,
    yearsCount: years.length,
    hasData: true,
    missingYears: findMissingYears(years),
  }
}

/**
 * Find missing years in a time series
 * @param {Array} years - Array of years
 * @returns {Array} Missing years
 */
function findMissingYears(years) {
  if (years.length < 2) return []

  const missing = []
  const sortedYears = years.map(y => parseInt(y)).sort((a, b) => a - b)

  for (let i = sortedYears[0]; i <= sortedYears[sortedYears.length - 1]; i++) {
    if (!sortedYears.includes(i)) {
      missing.push(i)
    }
  }

  return missing
}

/**
 * Prepare enrollment trend data for chart
 * @param {Array} rawData - Raw enrollment data
 * @returns {Array} Processed data ready for EnrollmentTrendsChart
 */
export function prepareEnrollmentTrendData(rawData) {
  if (!rawData || rawData.length === 0) return []

  // Aggregate by year, gender, and calculate totals
  return rawData.map(record => ({
    year: record.year,
    gender: record.gender || 'Unknown',
    total:
      parseFloat(record.total) ||
      Object.keys(record)
        .filter(key => key.startsWith('p') || key.startsWith('s'))
        .reduce((sum, key) => sum + (parseFloat(record[key]) || 0), 0),
  }))
}

/**
 * Prepare institution count trend data for chart
 * @param {Array} rawData - Raw institution count data
 * @returns {Array} Processed data ready for InstitutionGrowthChart
 */
export function prepareInstitutionTrendData(rawData) {
  if (!rawData || rawData.length === 0) return []

  return rawData.map(record => ({
    year: record.year,
    level: record.level || 'Unknown',
    ownership: record.ownership || 'Unknown',
    value: parseInt(record.value) || 0,
  }))
}

/**
 * Prepare PLE performance trend data for chart
 * @param {Array} rawData - Raw PLE data
 * @returns {Array} Processed data ready for PLEPerformanceTrendsChart
 */
export function preparePLETrendData(rawData) {
  if (!rawData || rawData.length === 0) return []

  return rawData.map(record => ({
    year: record.year,
    grade: record.grade,
    gender: record.gender || 'Unknown',
    value: parseInt(record.value) || 0,
  }))
}
